import React, { useContext } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Button, Divider, Grid, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { PermissionedDisplay, SectionGridContainer } from "../../components/Layout";
import { KeyValueDisplay } from "../../components/Typography";
import { RiderContext } from "../../context";
import { StringUtils } from "../../util";
import { useTestIds } from "../../hooks/useTestIds";
import Permissions from "../../permissions";

const { formatNumber } = StringUtils;

const useStyles = makeStyles((theme) => ({
  divider: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  walletBlock: {
    margin: `${theme.spacing(2)}px 0`,
  },
  riderRow: {
    paddingBottom: theme.spacing(1),
  },
  riderButton: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 0,
  },
}));

const WalletWidget = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { riderId } = useParams();

  const testIds = useTestIds("rider-profile-wallet-widget");
  const { wallet, activePass, customerCoupons } = useContext(RiderContext);

  const activePassTooltip = `Purchase date: ${DateTime.fromISO(
    activePass?.purchase_date,
  ).toLocaleString(DateTime.DATE_MED)}\nExpiration date: ${DateTime.fromISO(
    activePass?.expiration_date,
  ).toLocaleString(DateTime.DATE_MED)}`;

  return (
    <Grid className={classes.walletBlock}>
      <SectionGridContainer spaceContents testId={testIds.wrapper}>
        <Grid item container alignItems="center" xs={12}>
          <Grid item xs={9}>
            <Typography variant="h5" data-testid={testIds.title}>
              {t("Riders.Wallet.Wallet")}
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.divider}>
          <Divider />
        </Grid>
        <KeyValueDisplay
          label={t("Riders.Wallet.Balance")}
          text={formatNumber(wallet?.balance?.total, wallet?.currency)}
          testIds={{
            label: testIds.balanceLabel,
            text: testIds.balanceAmount,
          }}
          labelMinWidth="50%"
        />
        <KeyValueDisplay
          label={t("Riders.Wallet.Deposit")}
          text={formatNumber(wallet?.balance?.real, wallet?.currency)}
          testIds={{
            label: testIds.depositLabel,
            text: testIds.depositAmount,
          }}
          labelMinWidth="50%"
        />
        <KeyValueDisplay
          label={t("Riders.Wallet.Bonus")}
          text={formatNumber(wallet?.balance?.bonus, wallet?.currency)}
          testIds={{
            label: testIds.bonusLabel,
            text: testIds.bonusAmount,
          }}
          labelMinWidth="50%"
        />
        <Grid item style={{ marginTop: theme.spacing(1) }}>
          <Button
            data-testid={testIds.manageWalletButton}
            className={classes.riderButton}
            onClick={() =>
              history.push(`/riders/${riderId}/wallet/bonus`, { background: location })
            }
          >
            {t("Riders.Wallet.ManageWallet")}
          </Button>
        </Grid>

        <PermissionedDisplay
          permission={[
            Permissions.feature.passes,
            Permissions.readPasses,
            Permissions.readPassOffers,
            Permissions.readPassOfferTypes,
          ]}
        >
          <Grid item xs={12} className={classes.divider}>
            <Divider />
          </Grid>
          <KeyValueDisplay
            label={t("Riders.Wallet.ActivePass")}
            text={
              activePass
                ? capitalize(activePass?.pass_offer?.pass_offer_type.offer_type)
                : t("Riders.Wallet.None")
            }
            tooltip={activePass && activePassTooltip}
            tooltipPlacement="right"
            testIds={{
              label: testIds.activePassLabel,
              text: testIds.activePassValue,
              tooltipIcon: testIds.activePassTooltipIcon,
            }}
            labelMinWidth="50%"
          />

          {activePass && (
            <>
              <KeyValueDisplay
                label={t("Riders.Wallet.TimeRemaining")}
                text={StringUtils.countDown(activePass.expiration_date)}
                testIds={{
                  label: testIds.timeRemainingLabel,
                  text: testIds.timeRemainingValue,
                }}
                labelMinWidth="50%"
              />
              <KeyValueDisplay
                label={t("Riders.Wallet.PassTripsRemaining")}
                text={
                  activePass.pass_offer?.pass_offer_type?.max_rides
                    ? `${activePass.rides_remaining} / ${activePass.pass_offer?.pass_offer_type?.max_rides}`
                    : t("Riders.Wallet.Unlimited")
                }
                testIds={{
                  label: testIds.passTripsRemainingLabel,
                  text: testIds.passTripsRemainingValue,
                }}
                labelMinWidth="50%"
              />
            </>
          )}

          <PermissionedDisplay permission={[Permissions.writePasses]}>
            <Grid item style={{ marginTop: theme.spacing(1) }}>
              {activePass ? (
                <Button
                  data-testid={testIds.managePassButton}
                  className={classes.riderButton}
                  onClick={() =>
                    history.push(`/riders/${riderId}/wallet/manage-pass`, {
                      background: location,
                    })
                  }
                >
                  {t("Riders.WalletTransactions.ManagePass")}
                </Button>
              ) : (
                <Button
                  data-testid={testIds.addPassButton}
                  className={classes.riderButton}
                  onClick={() =>
                    history.push(`/riders/${riderId}/wallet/add-pass`, {
                      background: location,
                    })
                  }
                >
                  {t("Riders.Wallet.AddPass")}
                </Button>
              )}
            </Grid>
          </PermissionedDisplay>
        </PermissionedDisplay>
        <Grid item xs={12} className={classes.divider}>
          <Divider />
        </Grid>
        <KeyValueDisplay
          label={t("Riders.Wallet.Promos")}
          text={!customerCoupons || customerCoupons.length === 0 ? t("Riders.Wallet.None") : null}
          testIds={{
            label: testIds.activePromosLabel,
            text:
              !customerCoupons || customerCoupons.length === 0 ? testIds.activePromosValue : null,
          }}
          labelMinWidth="50%"
        />

        {customerCoupons?.length > 0 && (
          <Grid item xs={12}>
            {customerCoupons.map((coupon, idx) => (
              <Typography
                data-testid={`${testIds.activePromosValue}-${idx}`}
                key={`${coupon.id}-${Math.random()}`}
                className={classes.riderRow}
              >
                {`${coupon.code} (${
                  coupon.type === "discount"
                    ? `${formatNumber(coupon.percent_off, "PERCENT_WHOLE")} `
                    : ""
                }${t(`Riders.Wallet.CouponTypes.${coupon.type}`)} – ${DateTime.fromISO(
                  coupon.expired,
                ).toLocaleString(DateTime.DATE_MED)})`}
              </Typography>
            ))}
          </Grid>
        )}
      </SectionGridContainer>
    </Grid>
  );
};

export default WalletWidget;
